import { useState, useRef, useCallback } from 'react';
import { Parent } from '../../models/Parent.js';
import { RelativePosition } from '../../models/RelativePosition.js';
import { getAngleAndDistance } from '../../utils/mindmap/geometry';
import { COLORS } from '../../config/colors';
import { buildDeleteSubtreeCommand } from '../../utils/mindmap/mapCommands.js';

/**
 * Custom hook for managing nodes, relationships, and mind map operations
 * Eliminates the need for synthetic event dispatching and centralizes node logic
 */
export const useNodeManagement = ({ containerRef, saveVersion, resolveCollisions, currentUserId = null, currentUserName = null }) => {
  const [nodes, setNodes] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [root, setRoot] = useState(null);
  const [mindmapId, setMindmapId] = useState(null); // Track the current mindmap ID


  const parentMap = useRef(new Map());
  const lastAddedNodeRef = useRef(null); // Track the last added node

  // Helper functions
  const getNode = useCallback((id) => {
    return nodes.find(n => n.id === id);
  }, [nodes]);

  const getSelectedNode = useCallback(() => {
    return getNode(selectedId);
  }, [getNode, selectedId]);

  const getParentNode = useCallback((childId) => {
    const relationship = relationships.find(r => r.childId === childId);
    return relationship ? nodes.find(n => n.id === relationship.parentId) : null;
  }, [relationships, nodes]);

  const getChildren = useCallback((parentId) => {
    const result = [];
    const visited = new Set([parentId]);
    const collect = (id) => {
      const children = relationships
        .filter(r => r.parentId === id)
        .map(r => r.childId)
        .sort((left, right) => String(left).localeCompare(String(right)));

      for (let child of children) {
        if (!child || visited.has(child)) continue;
        visited.add(child);
        result.push(child);
        collect(child);
      }
    };
    collect(parentId);
    return result;
  }, [relationships]);

  const snapAngleToCardinal = useCallback((angle) => {
    const normalized = ((angle % 360) + 360) % 360;
    if (normalized >= 315 || normalized < 45) return 0;
    else if (normalized >= 45 && normalized < 135) return 90;
    else if (normalized >= 135 && normalized < 225) return 180;
    else return 270;
  }, []);

  // Helper to check if a position would collide with existing nodes
  const wouldCollideWithNodes = useCallback((testPosition, existingNodes, minDistance = 80) => {
    // Assume typical node dimensions
    const nodeWidth = 200;
    const nodeHeight = 60;

    const testBounds = {
      left: testPosition.x,
      top: testPosition.y,
      right: testPosition.x + nodeWidth,
      bottom: testPosition.y + nodeHeight,
      centerX: testPosition.x + nodeWidth / 2,
      centerY: testPosition.y + nodeHeight / 2
    };

    for (const node of existingNodes) {
      const nodeBounds = {
        left: node.position.x,
        top: node.position.y,
        right: node.position.x + nodeWidth,
        bottom: node.position.y + nodeHeight,
        centerX: node.position.x + nodeWidth / 2,
        centerY: node.position.y + nodeHeight / 2
      };

      // Check if bounding boxes overlap with minDistance padding
      const overlap = !(
        testBounds.right + minDistance < nodeBounds.left ||
        nodeBounds.right + minDistance < testBounds.left ||
        testBounds.bottom + minDistance < nodeBounds.top ||
        nodeBounds.bottom + minDistance < testBounds.top
      );

      if (overlap) return true;
    }

    return false;
  }, []);

  // Helper to check if the line from parent to child would pass under other nodes
  const wouldLineCrossNodes = useCallback((parentPosition, childPosition, existingNodes) => {
    const nodeWidth = 200;
    const nodeHeight = 60;

    const isPointInsideRect = (x, y, rect) => (
      x > rect.left && x < rect.right &&
      y > rect.top && y < rect.bottom
    );

    for (const node of existingNodes) {
      // Build approximate bounds for this node
      const rect = {
        left: node.position.x,
        top: node.position.y,
        right: node.position.x + nodeWidth,
        bottom: node.position.y + nodeHeight
      };

      // Sample a few points along the line (excluding exact endpoints)
      const steps = 8;
      for (let i = 1; i < steps; i++) {
        const t = i / steps;
        const x = parentPosition.x + (childPosition.x - parentPosition.x) * t;
        const y = parentPosition.y + (childPosition.y - parentPosition.y) * t;

        if (isPointInsideRect(x, y, rect)) {
          return true;
        }
      }
    }

    return false;
  }, []);

  // Node operations
  const createNode = useCallback(({ position, parentId = null, text = '', autoFocus = false, customPosition = null }) => {
    const id = crypto.randomUUID();
    const currentNode = parentId ? getNode(parentId) : null;

    let finalPosition = position;
    let parent = null;

    // If customPosition is provided, use it instead of automatic positioning
    if (customPosition) {
      finalPosition = customPosition;
    } else if (currentNode) {
      const currentParent = getParentNode(currentNode.id);
      parent = parentMap.current.get(currentNode.id);

      if (!parent) {
        parent = new Parent(currentNode.position, currentNode.id);
        parentMap.current.set(parent.id, parent);
      }

      // Determine preferred direction based on parent's relationship to grandparent
      let preferredAngle = 0;
      let baseRadius = 210;

      if (currentParent) {
        // Calculate angle and distance from grandparent to parent
        const { angle: angleFromGrandparent, distance: distanceFromGrandparent } = getAngleAndDistance(
          currentParent.position.x,
          currentParent.position.y,
          currentNode.position.x,
          currentNode.position.y
        );

        // Children should continue in the same general direction
        preferredAngle = snapAngleToCardinal(angleFromGrandparent);

        // Base radius starts from how far the parent is from its own parent
        // but slightly closer (about 3/4 of that distance)
        const defaultHorizontalRadius = 210;
        const defaultVerticalRadius = 150;
        const preferredBase = (preferredAngle === 90 || preferredAngle === 270)
          ? defaultVerticalRadius
          : defaultHorizontalRadius;

        const rawRadius = distanceFromGrandparent || preferredBase;
        baseRadius = rawRadius * 0.75;
      } else {
        // No grandparent: keep children relatively close by default
        preferredAngle = 0;
        baseRadius = 210 * 0.75;
      }

      // Clamp radius so children are not too close or too far
      baseRadius = Math.max(90, Math.min(baseRadius, 260));

      // Get all existing nodes to check for collisions
      const existingNodes = nodes.filter(n => n.id !== id);

      // Try to find a collision-free position
      // Strategy: Try multiple angles around the preferred direction
      // and multiple radii, checking actual node overlaps
      let angle = preferredAngle;
      let radius = baseRadius;
      let foundPosition = false;
      const maxAttempts = 50; // Prevent infinite loops
      let attempt = 0;

      // Define search pattern: angles near preferred direction first
      const angleOffsets = [0, 30, -30, 15, -15, 45, -45, 60, -60, 90, -90, 75, -75];
      const radiusMultipliers = [1, 0.85, 1.15, 1.4, 1.7, 2.1];

      outerLoop:
      for (const radiusMult of radiusMultipliers) {
        const testRadius = baseRadius * radiusMult;

        for (const angleOffset of angleOffsets) {
          if (attempt++ > maxAttempts) break outerLoop;

          const testAngle = (preferredAngle + angleOffset + 360) % 360;

          // Check if this angle/radius combo is too close to existing children
          if (parent.getChildAtAngleRange(testAngle, 25, testRadius, 30)) {
            continue;
          }

          // Calculate actual position
          const relativePos = RelativePosition.fromAngleAndDistance(testAngle, testRadius);
          const testPosition = relativePos.getPointFrom(currentNode.position.x, currentNode.position.y);

          // Check if this position would collide or cause the line to pass under another node
          if (!wouldCollideWithNodes(testPosition, existingNodes, 40) &&
              !wouldLineCrossNodes(currentNode.position, testPosition, existingNodes)) {
            angle = testAngle;
            radius = testRadius;
            foundPosition = true;
            break outerLoop;
          }
        }
      }

      // If we still haven't found a good position, fall back to increasing radius
      if (!foundPosition) {
        angle = preferredAngle;
        radius = baseRadius;
        let spiralAngle = 0;

        for (let r = baseRadius; r < baseRadius * 3; r += 80) {
          for (let a = 0; a < 360; a += 20) {
            const testAngle = (preferredAngle + a + spiralAngle) % 360;
            const relativePos = RelativePosition.fromAngleAndDistance(testAngle, r);
            const testPosition = relativePos.getPointFrom(currentNode.position.x, currentNode.position.y);

            if (!wouldCollideWithNodes(testPosition, existingNodes, 40) &&
                !wouldLineCrossNodes(currentNode.position, testPosition, existingNodes)) {
              angle = testAngle;
              radius = r;
              foundPosition = true;
              break;
            }
          }
          if (foundPosition) break;
          spiralAngle += 15; // Slight spiral to spread nodes out
        }
      }

      const relativePosition = RelativePosition.fromAngleAndDistance(angle, radius);
      finalPosition = relativePosition.getPointFrom(currentNode.position.x, currentNode.position.y);
      parent.addChild(id, relativePosition);
      parentMap.current.set(parent.id, parent);
    }

    const newNode = {
      id,
      text,
      position: finalPosition,
      color: (currentNode && currentNode.color) ? currentNode.color : COLORS[0].gradient,
      isRoot: !parent,
      collapsedChildren: null,
      autoFocus, // Flag for auto-focusing instead of synthetic events
      userId: currentUserId || null,
      creatorName: currentUserName || null,
      // Task metadata (optional per-node)
      isTask: false,
      taskDone: false,
    };

    setNodes(prev => [...prev, newNode]);

    if (parentId) {
      setRelationships(prev => [...prev, { parentId, childId: id }]);
    }

    setSelectedId(id);

    // Track this as the last added node
    lastAddedNodeRef.current = { id, parentId, position: finalPosition };

    // Only run collision resolution if we're not using custom positioning
    if (!customPosition) {
      setTimeout(() => {
        resolveCollisions?.();
        saveVersion?.();
      }, 0);
    } else {
      setTimeout(() => {
        saveVersion?.();
      }, 0);
    }

    return id;
  }, [
    getNode,
    getParentNode,
    snapAngleToCardinal,
    wouldCollideWithNodes,
    wouldLineCrossNodes,
    nodes,
    resolveCollisions,
    saveVersion,
    currentUserId,
    currentUserName,
  ]);

  const addChildNode = useCallback((customPosition = null) => {
    if (!selectedId) return;
    const containerRect = containerRef.current?.getBoundingClientRect();
    const position = {
      x: containerRect.width / 2 - 100,
      y: containerRect.height / 2
    };

    return createNode({
      position,
      parentId: selectedId,
      autoFocus: true,
      customPosition
    });
  }, [selectedId, containerRef, createNode]);

  const addSiblingNode = useCallback((customPosition = null) => {
    const currentNode = getSelectedNode();
    if (!currentNode) return;

    const parent = getParentNode(currentNode.id);
    if (!parent) return;

    setSelectedId(parent.id);

    // Use a small delay to ensure state update is processed
    setTimeout(() => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      const position = {
        x: containerRect.width / 2 - 100,
        y: containerRect.height / 2
      };

      createNode({
        position,
        parentId: parent.id,
        autoFocus: true,
        customPosition
      });
    }, 10);
  }, [getSelectedNode, getParentNode, createNode, containerRef]);

  const applyDeleteSubtreeCommand = useCallback((command) => {
    if (command?.type !== 'delete-subtree' || !Array.isArray(command.nodeIds)) {
      throw new Error('Invalid delete-subtree command');
    }
    const deletingIds = new Set(command.nodeIds);
    if (!deletingIds.size) throw new Error('Delete command requires at least one node');

    setNodes((previous) => previous.filter((node) => !deletingIds.has(node.id)));
    setRelationships((previous) => previous.filter((relationship) => (
      !deletingIds.has(relationship.parentId) && !deletingIds.has(relationship.childId)
    )));
    if (deletingIds.has(selectedId)) {
      setSelectedId(command.nextSelectedId || null);
    }
    return command.nextSelectedId || null;
  }, [selectedId]);

  const removeNode = useCallback((nodeId) => {
    let command;
    try {
      command = buildDeleteSubtreeCommand({ nodeId, rootId: root, nodes, relationships });
    } catch (error) {
      console.warn('Delete command rejected:', error.message);
      return null;
    }

    applyDeleteSubtreeCommand(command);

    setTimeout(() => {
      saveVersion?.();
    }, 0);

    return command.nextSelectedId;
  }, [applyDeleteSubtreeCommand, nodes, relationships, root, saveVersion]);

  const updateNodeText = useCallback((id, text) => {
    setNodes(prev => prev.map(n => (n.id === id ? { ...n, text, lastEditedBy: currentUserId } : n)));
  }, [currentUserId]);

  const updateNodePosition = useCallback((id, newPos, options = {}) => {
    const { animate = false } = options;

    setNodes(prevNodes => {
      const node = prevNodes.find(n => n.id === id);
      if (!node) return prevNodes;

      const delta = {
        x: newPos.x - node.position.x,
        y: newPos.y - node.position.y
      };

      const isDescendant = (parentId, childId) => {
        const directChildren = relationships
          .filter(r => r.parentId === parentId)
          .map(r => r.childId);
        if (directChildren.includes(childId)) return true;
        return directChildren.some(id => isDescendant(id, childId));
      };

      return prevNodes.map(n => {
        if (n.id === id || isDescendant(id, n.id)) {
          const updatedPos = {
            x: n.position.x + delta.x,
            y: n.position.y + delta.y
          };

          return {
            ...n,
            position: updatedPos,
            animate: animate ? true : n.animate
          };
        } else {
          return n;
        }
      });
    });
  }, [relationships]);

  const setNodeColor = useCallback((nodeId, color) => {
    setNodes(prev => prev.map(n =>
      n.id === nodeId ? { ...n, color: color.gradient, lastEditedBy: currentUserId } : n
    ));
    setTimeout(saveVersion, 0);
  }, [saveVersion, currentUserId]);

  // Toggle whether a node is treated as a task
  const toggleTaskFlag = useCallback((nodeId) => {
    setNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      if (!n.isTask) {
        return {
          ...n,
          isTask: true,
          taskDone: false,
          taskCompletedAt: null,
          taskCompletedByUserId: null,
          lastEditedBy: currentUserId,
        };
      }
      // Turning off task mode also clears completion state
      return {
        ...n,
        isTask: false,
        taskDone: false,
        taskCompletedAt: null,
        taskCompletedByUserId: null,
        lastEditedBy: currentUserId,
      };
    }));
  }, [currentUserId]);

  // Toggle completion state for a task node
  const toggleTaskDone = useCallback((nodeId) => {
    setNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      if (!n.isTask) return n;
      return { ...n, taskDone: !n.taskDone, lastEditedBy: currentUserId };
    }));
  }, [currentUserId]);

  const clearAutoFocus = useCallback(() => {
    setNodes(prev => prev.map(node =>
      node.autoFocus ? { ...node, autoFocus: false } : node
    ));
  }, []);

  const insertNodeWithRelationship = useCallback((command) => {
    const node = command?.node;
    const relationship = command?.relationship;
    if (!node?.id || !relationship?.parentId || relationship.childId !== node.id) {
      throw new Error('Invalid create-node command');
    }
    if (!getNode(relationship.parentId)) {
      throw new Error(`Parent node ${relationship.parentId} does not exist`);
    }
    if (getNode(node.id)) {
      throw new Error(`Node ${node.id} already exists`);
    }

    setNodes((previous) => [...previous, node]);
    setRelationships((previous) => [...previous, relationship]);
    setSelectedId(node.id);
    lastAddedNodeRef.current = {
      id: node.id,
      parentId: relationship.parentId,
      position: node.position,
    };
    return node.id;
  }, [getNode]);

  const applyMoveNodesCommand = useCallback((command) => {
    if (!Array.isArray(command?.positions) || command.positions.length === 0) {
      throw new Error('Invalid move command');
    }
    const updates = new Map();
    command.positions.forEach((entry) => {
      if (!entry?.id || !getNode(entry.id)) {
        throw new Error(`Node ${entry?.id || ''} does not exist`);
      }
      if (!Number.isFinite(entry.position?.x) || !Number.isFinite(entry.position?.y)) {
        throw new Error(`Node ${entry.id} requires a finite position`);
      }
      updates.set(entry.id, entry);
    });

    setNodes((previous) => previous.map((node) => (
      updates.has(node.id)
        ? {
            ...node,
            position: updates.get(node.id).position,
            ...(updates.get(node.id).layout ? { layout: updates.get(node.id).layout } : {}),
            lastEditedBy: currentUserId,
          }
        : node
    )));
    return command.nodeId;
  }, [currentUserId, getNode]);

  const applyReparentSubtreeCommand = useCallback((command) => {
    const removed = command?.removeRelationship;
    const added = command?.relationship;
    if (command?.type !== 'reparent-subtree' || !removed?.parentId || !added?.parentId) {
      throw new Error('Invalid reparent command');
    }
    if (removed.childId !== command.nodeId || added.childId !== command.nodeId) {
      throw new Error('Invalid reparent relationships');
    }
    if (!getNode(command.nodeId) || !getNode(added.parentId)) {
      throw new Error('Reparent command references a missing node');
    }
    if (!relationships.some((relationship) => (
      relationship.parentId === removed.parentId && relationship.childId === removed.childId
    ))) {
      throw new Error('Existing parent relationship does not exist');
    }

    applyMoveNodesCommand(command);
    setRelationships((previous) => [
      ...previous.filter((relationship) => relationship.childId !== command.nodeId),
      added,
    ]);
    return command.nodeId;
  }, [applyMoveNodesCommand, getNode, relationships]);

  const reparentNode = useCallback((nodeId, newParentId) => {
    const node = getNode(nodeId);
    const newParent = getNode(newParentId);

    if (!node || !newParent) return false;

    // Prevent cycles: check if newParent is a descendant of node
    const descendants = getChildren(nodeId);
    if (descendants.includes(newParentId)) {
      console.warn('Cannot reparent: would create a cycle');
      return false;
    }

    // Prevent self-parenting
    if (nodeId === newParentId) {
      console.warn('Cannot reparent: node cannot be its own parent');
      return false;
    }

    // Update relationships in a single state update to avoid inconsistency
    setRelationships(prev => {
      // Remove old relationship and add new one in one operation
      const filtered = prev.filter(r => r.childId !== nodeId);
      return [...filtered, { parentId: newParentId, childId: nodeId }];
    });

    // Position the node near the new parent (we can adjust this later)
    const newPosition = {
      x: newParent.position.x + 150,
      y: newParent.position.y + 50
    };

    // Use a small delay to ensure relationships are updated before positioning
    setTimeout(() => {
      updateNodePosition(nodeId, newPosition);
      setTimeout(() => {
        saveVersion?.();
      }, 10);
    }, 10);

    return true;
  }, [getNode, getChildren, updateNodePosition, saveVersion]);

  return {
    // State
    nodes,
    relationships,
    selectedId,
    root,
    mindmapId,

    // Setters
    setNodes,
    setRelationships,
    setSelectedId,
    setRoot,
    setMindmapId,

    // Getters
    getNode,
    getSelectedNode,
    getParentNode,
    getChildren,

    // Operations
    createNode,
    addChildNode,
    addSiblingNode,
    insertNodeWithRelationship,
    applyMoveNodesCommand,
    applyReparentSubtreeCommand,
    applyDeleteSubtreeCommand,
    removeNode,
    updateNodeText,
    updateNodePosition,
    setNodeColor,
    toggleTaskFlag,
    toggleTaskDone,
    clearAutoFocus,
    reparentNode,

    // Internal refs
    parentMap,
    lastAddedNodeRef
  };
};;
